<template>
  <Loading v-if="$store.getters.getUserLoading" />
  
  <div v-else class="signup-page">
    <div class="signup-container">
      <div class="signup-header">
        <div class="user-avatar-section" v-if="auth0User">
          <b-avatar :src="auth0User.picture" size="80px" class="user-avatar" />
        </div>
        <div class="title-section">
          <img src="@/assets/logos/PTLogo-NoText-Cropped.png" alt="Principal Task" class="pt-logo" />
          <div>
            <h2 class="signup-title">You're almost done</h2>
            <p class="signup-subtitle">Complete your profile to get started</p>
          </div>
        </div>
      </div>
      
      <b-form @submit.prevent="onSubmit" class="signup-form">
        
        <!-- Personal Details -->
        <b-form-row>
          <b-col>
            <b-form-group
              id="signup-firstName-group"
              label-for="firstName"
              description="Your first name, or given name."
              :state="validation.firstName"
              valid-feedback="Looks good!"
              invalid-feedback="This information is required."
            >
              <label class="detail-label" for="firstName">FIRST NAME</label>
              <b-form-input
                id="firstName"
                v-model.trim="userData.firstName"
                :state="validation.firstName"
                size="sm"
              />
            </b-form-group>
          </b-col>

          <b-col>
            <b-form-group
              id="signup-lastName-group"
              label-for="lastName"
              description="Your last name, surname, or family name."
              :state="validation.lastName"
              valid-feedback="Looks good!"
              invalid-feedback="This information is required."
            >
              <label class="detail-label" for="lastName">LAST NAME</label>
              <b-form-input
                id="lastName"
                v-model.trim="userData.lastName"
                :state="validation.lastName"
                size="sm"
              />
            </b-form-group>
          </b-col>
        </b-form-row>

        <b-form-row class="mt-3">
          <b-col cols="6">
            <b-form-group
              id="signup-goesBy-group"
              label-for="goesBy"
              description="Your nickname or alias."
              :state="validation.goesBy"
              valid-feedback="Looks good!"
            >
              <label class="detail-label" for="goesBy">PREFERRED NAME</label>
              <b-form-input
                id="goesBy"
                v-model.trim="userData.goesBy"
                :state="validation.goesBy"
                size="sm"
              />
            </b-form-group>
          </b-col>

          <b-col>
            <b-form-group
              id="signup-email-group"
              label-for="email"
              description="We'll use this for important account notifications."
            >
              <label class="detail-label" for="email">EMAIL</label>
              <b-form-input
                id="email"
                v-model="userData.email"
                type="email"
                disabled
                size="sm"
              />
            </b-form-group>
          </b-col>
        </b-form-row>

        <b-form-row class="mt-3">
          <b-col>
            <b-form-group
              id="signup-timezone-group"
              label-for="timezone"
              description="The timezone in which you usually reside."
              :state="validation.timezone"
              valid-feedback="Looks good!"
              invalid-feedback="This information is required."
            >
              <label class="detail-label" for="timezone">TIMEZONE</label>
              <b-form-select
                id="timezone"
                v-model.trim="userData.timezone"
                :state="validation.timezone"
                :options="validTimezones"
                value-field="item"
                text-field="name"
                size="sm"
              />
            </b-form-group>
          </b-col>

          <b-col>
            <b-form-group
              id="signup-country-group"
              label-for="country"
              description="The country in which you usually reside."
              :state="validation.country"
              valid-feedback="Looks good!"
              invalid-feedback="This information is required."
            >
              <label class="detail-label" for="country">COUNTRY</label>
              <b-form-select
                id="country"
                v-model.trim="userData.country"
                :state="validation.country"
                :options="countries"
                value-field="Code"
                text-field="Name"
                size="sm"
              />
            </b-form-group>
          </b-col>
        </b-form-row>

        <b-form-row class="mt-3">
          <b-col>
            <b-form-group
              id="signup-referral-group"
              label-for="referralSource"
              description="Help us understand how you found Principal Task."
            >
              <label class="detail-label" for="referralSource">HOW DID YOU HEAR ABOUT US?</label>
              <b-form-select
                id="referralSource"
                v-model="userData.referralSource"
                :options="referralOptions"
                size="sm"
              />
            </b-form-group>
          </b-col>
        </b-form-row>

        <!-- Terms of Service -->
        <b-card no-body class="mt-4 terms-card">
          <b-card-body>
            <b-form-checkbox
              id="termsAccepted"
              :model-value="termsAccepted"
              @update:modelValue="termsAccepted = $event"
              :value="true"
              :unchecked-value="false"
              :state="validation.terms"
            >
              I agree to the 
              <a href="/user-agreement" target="_blank">Terms of Service</a> 
              and 
              <a href="/privacy-policy" target="_blank">Privacy Policy</a>
            </b-form-checkbox>
            <b-form-invalid-feedback :state="validation.terms">
              You must accept the terms to continue
            </b-form-invalid-feedback>
          </b-card-body>
        </b-card>

        <!-- Action Buttons -->
        <b-row class="mt-4">
          <b-col cols="12" style="text-align:right;">
            <b-button
              type="submit"
              variant="success"
              size="lg"
              :disabled="submitting"
              class="submit-btn"
            >
              <span v-if="submitting" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              {{ submitting ? 'Creating Your Account...' : 'Get Started' }}
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </div>
  </div>
</template>

<script>
// 20260125 -- Copilot (Claude Sonnet 4.5) -- Redesigned signup page for reduced drop-off
// Consolidated from 1438 lines to ~400 lines, single page, critical fields prominent
// Matches ProfileForm.vue styling, uses Bootstrap collapse, no sidebar
import Loading from '@/components/Loading.vue'
import UserService from '@/services/UserService'
import FolderService from '@/services/FolderService'
import ContextService from '@/services/ContextService'
import TaskService from '@/services/TaskService'
import LocalAuth0Service from '@/services/LocalAuth0Service'
import Logger from '@/utils/Logger'
import countries from '@/mixins/countries'
import moment from 'moment'
import momentTZ from 'moment-timezone'
import { RRule } from 'rrule'
import { v4 as UUIDService } from 'uuid'

const log = Logger.create('Signup', { showCaller: true, showLineNumbers: true })

export default {
  name: 'Signup',
  components: {
    Loading
  },
  mixins: [countries],
  data() {
    return {
      auth0User: null,
      userData: {
        email: '',
        firstName: '',
        lastName: '',
        goesBy: '',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York',
        country: '',
        referralSource: ''
      },
      termsAccepted: false,
      submitting: false,
      validation: {
        firstName: null,
        lastName: null,
        goesBy: null,
        timezone: null,
        country: null,
        terms: null
      },
      referralOptions: [
        { value: '', text: 'Select one...' },
        { value: 'search', text: 'Search Engine' },
        { value: 'social', text: 'Social Media' },
        { value: 'friend', text: 'Friend or Colleague' },
        { value: 'blog', text: 'Blog or Article' },
        { value: 'other', text: 'Other' }
      ]
    }
  },
  computed: {
    validTimezones() {
      return momentTZ.tz.names().map(tz => ({
        item: tz,
        name: tz
      }))
    }
  },
  async created() {
    // 20260128 -- Copilot (Claude Sonnet 4.5) -- Check if user already has complete profile
    // WHY: Mobile app users who complete registration there shouldn't be forced through signup again
    try {
      const token = await this.$auth.getTokenSilently()
      await this.$store.dispatch('setUserToken', token)
      await this.$store.dispatch('loadUser')
      
      // If user loaded successfully and has required fields, redirect to dashboard
      const user = this.$store.getters.getUser
      if (user && user.firstName && user.lastName && user.timezone) {
        log.info('User already has complete profile, redirecting to dashboard')
        this.$router.push({name: 'dashboard'})
        return
      }
    } catch (error) {
      log.debug('User needs to complete signup', error)
    }
    
    await this.loadUserData()
  },
  methods: {
    async loadUserData() {
      try {
        this.auth0User = await this.$auth.user
        if (this.auth0User) {
          this.userData.email = this.auth0User.email
          // Pre-fill from Auth0 if available
          if (this.auth0User.given_name) this.userData.firstName = this.auth0User.given_name
          if (this.auth0User.family_name) this.userData.lastName = this.auth0User.family_name
        }
      } catch (error) {
        log.error('Error loading Auth0 user data', error)
      }
    },
    validateForm() {
      let valid = true
      
      // Validate first name
      this.validation.firstName = this.userData.firstName.length > 0 ? true : false
      if (!this.validation.firstName) valid = false
      
      // Validate last name
      this.validation.lastName = this.userData.lastName.length > 0 ? true : false
      if (!this.validation.lastName) valid = false
      
      // Validate timezone
      this.validation.timezone = this.userData.timezone.length > 0 ? true : false
      if (!this.validation.timezone) valid = false
      
      // Validate country
      this.validation.country = this.userData.country.length > 0 ? true : false
      if (!this.validation.country) valid = false
      
      // Validate terms
      this.validation.terms = this.termsAccepted ? true : false
      if (!this.validation.terms) valid = false
      
      return valid
    },
    async onSubmit() {
      if (!this.validateForm()) {
        this.$bvToast.toast('Please complete all required fields', {
          title: 'Validation Error',
          variant: 'danger',
          solid: true
        })
        return
      }
      
      this.submitting = true
      
      try {
        const token = await this.$auth.getTokenSilently()
        
        // Prepare complete user data with defaults from old signup
        const completeUserData = {
          ...this.userData,
          signupDate: Number(moment().format('x')),
          version: '0.0.0',
          loadDefaults: 'Y',
          subscriptionTier: 'free',
          subscriptionLevel: '2020-free',
          privacyPolicy: 'Y',
          userAgreement: 'Y'
        }
        
        // Create user profile
        const result = await UserService.createUser(completeUserData, token)
        
        if (result.data.result && result.data.result.successful) {
          log.info('User created successfully')
          
          const newUser = result.data.result.data
          
          // Update Auth0 app_metadata with PT user ID
          const appMetaData = this.auth0User['https://www.principaltask.com/app_metadata'] || {}
          
          if (!appMetaData.pt_user_id || appMetaData.pt_user_id != newUser._id) {
            const updates = {
              "app_metadata": {
                "pt_user_id": newUser._id
              }
            }
            
            await LocalAuth0Service.updateUser(token, updates)
            const updatedToken = await this.$auth.getTokenSilently()
            await this.$store.dispatch('setUserToken', updatedToken)
          } else {
            await this.$store.dispatch('setUserToken', token)
          }
          
          await this.$store.dispatch('setUser', newUser)
          
          // Set default user preferences
          const prefsTemplate = {
            defaultTaskOptions: {
              priority: 'medium',
              status: 'planning',
              folder: 'Inbox',
              context: null,
              start: 1,
              due: 1
            },
            taskFilter: {
              completed: 'N',
              context: null,
              folder: null,
              quickFilter: null,
              location: 'Signup.vue'
            },
            taskSortOrder: {
              first: 'title', 
              second: 'start', 
              third: 'due',
              firstDirection: 1,
              secondDirection: 1,
              thirdDirection: 1
            }
          }
          
          await this.$store.dispatch('setUserPrefs', prefsTemplate)
          
          // Create default folders
          const defaultFolders = [
            { name: 'Inbox', group: 'Default', private: 'Y', active: 'Y', customOrder: 0, owner: newUser._id },
            { name: 'Family', group: 'Default', private: 'Y', active: 'Y', customOrder: 5, owner: newUser._id },
            { name: 'Finances', group: 'Default', private: 'Y', active: 'Y', customOrder: 10, owner: newUser._id },
            { name: 'Personal', group: 'Default', private: 'Y', active: 'Y', customOrder: 15, owner: newUser._id },
            { name: 'Professional', group: 'Default', private: 'Y', active: 'Y', customOrder: 20, owner: newUser._id }
          ]
          
          await FolderService.addFolders(defaultFolders, token)
          
          // Create default contexts
          const defaultContexts = [
            { name: '@Home', private: 'Y', active: 'Y', owner: newUser._id },
            { name: '@School', private: 'Y', active: 'Y', owner: newUser._id },
            { name: '@Shopping', private: 'Y', active: 'Y', owner: newUser._id },
            { name: '@Work', private: 'Y', active: 'Y', owner: newUser._id }
          ]
          
          await ContextService.addContexts(defaultContexts, token)
          
          // Create default tasks
          const today = moment()
          today.hour(0)
          today.minute(0)
          today.second(0)
          today.millisecond(0)
          
          const tomorrow = moment()
          tomorrow.hour(0)
          tomorrow.minute(0)
          tomorrow.second(0)
          tomorrow.millisecond(0)
          tomorrow.add(1, 'days')
          
          const overmorrow = moment()
          overmorrow.hour(0)
          overmorrow.minute(0)
          overmorrow.second(0)
          overmorrow.millisecond(0)
          overmorrow.add(2, 'days')
          
          const defaultTasks = [
            {
              title: '#1. Add every task I can think of to Principal Task',
              start: Number(today.format('x')),
              due: Number(today.format('x')),
              added: Number(today.format('x')),
              completed: 'N',
              priority: 'top',
              star: 'N',
              status: 'next',
              folder: 'Inbox',
              notes: 'Gather all of the things I have to do. Whether it\'s stored in my mind, in my email, on a sticky note, or wherever it may be.',
              owner: newUser._id,
              id: UUIDService()
            },
            {
              title: '#2. Refine',
              start: Number(tomorrow.format('x')),
              due: Number(tomorrow.format('x')),
              added: Number(today.format('x')),
              completed: 'N',
              priority: 'top',
              star: 'N',
              status: 'next',
              folder: 'Inbox',
              notes: 'Add detail to each task: update the notes with everything you know (or don\'t know) about it.',
              owner: newUser._id,
              id: UUIDService()
            },
            {
              title: '#3. Organize',
              start: Number(overmorrow.format('x')),
              due: Number(overmorrow.format('x')),
              added: Number(today.format('x')),
              completed: 'N',
              priority: 'top',
              star: 'N',
              status: 'next',
              folder: 'Inbox',
              notes: 'Review each task and put it in a folder, assign it a context, and set a time to start and complete it.',
              owner: newUser._id,
              id: UUIDService()
            },
            {
              title: 'Process Inbox',
              start: Number(overmorrow.format('x')),
              due: Number(overmorrow.format('x')),
              added: Number(today.format('x')),
              completed: 'N',
              repeat: {
                freq: RRule.DAILY,
                interval: 2
              },
              priority: 'top',
              star: 'N',
              status: 'next',
              folder: 'Inbox',
              notes: 'Review each task I\'ve collected and: \n* add detail: update the notes with everything I know (or don\'t know) about it\n* organize it: put it in a folder, assign it a context, and set a time to start and complete it.',
              owner: newUser._id,
              id: UUIDService()
            }
          ]
          
          await TaskService.addTasks(defaultTasks, token)
          
          // Set user and prefs in store FIRST, then load additional data
          await this.$store.dispatch('setUser', newUser)
          await this.$store.dispatch('setUserPrefs', prefsTemplate)
          
          // Now load additional data (contexts, folders)
          await this.$store.dispatch('loadContexts')
          await this.$store.dispatch('loadFolders')
          
          // Redirect to dashboard
          this.$router.push({ name: 'dashboard' })
        } else {
          throw new Error(result.data.result?.message || result.data.message || 'Failed to create user')
        }
      } catch (error) {
        log.error('Error creating user', error)
        this.$bvToast.toast(error.message || 'Failed to create your account. Please try again.', {
          title: 'Error',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.signup-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #4a5568 0%, #2d3748 100%);
  padding: 2rem 0;
  position: relative;
}

.signup-page::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml;utf8,<svg width="100" height="100" xmlns="http://www.w3.org/2000/svg"><defs><pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse"><path d="M 20 0 L 0 0 0 20" fill="none" stroke="rgba(255,255,255,0.05)" stroke-width="1"/></pattern></defs><rect width="100" height="100" fill="url(%23grid)" /></svg>');
  opacity: 0.3;
  pointer-events: none;
}

.signup-container {
  max-width: 650px;
  margin: 0 auto;
  padding: 0 1rem;
  position: relative;
  z-index: 1;
}

.signup-header {
  text-align: center;
  margin-bottom: 2rem;
}

.title-section {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-top: 1rem;
  animation: fadeInDown 0.6s ease 0.2s both;
}

.pt-logo {
  width: 50px;
  height: 50px;
  filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.2));
  background: white;
  border-radius: 8px;
  padding: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.user-avatar-section {
  margin-bottom: 0.5rem;
  animation: fadeInDown 0.6s ease 0.1s both;
}

.user-avatar {
  border: 4px solid white;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
}

.signup-title {
  font-size: 2rem;
  font-weight: 700;
  color: white;
  margin-bottom: 0.5rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  text-align: left;
}

.signup-subtitle {
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
  text-align: left;
}

.signup-form {
  background: white;
  border-radius: 16px;
  padding: 2.5rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
  animation: fadeInUp 0.6s ease 0.4s both;
}

.detail-label {
  font-size: 0.625rem;
  font-weight: 500;
  color: var(--text-secondary);
  margin-bottom: var(--spacing-xs);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  display: block;
}

.terms-card {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 1px solid #dee2e6;
}

.submit-btn {
  font-weight: 600;
  padding: 0.75rem 2.5rem;
  border-radius: 8px;
  box-shadow: 0 4px 14px rgba(40, 167, 69, 0.4);
  transition: all 0.3s ease;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(40, 167, 69, 0.5);
}

.submit-btn:active:not(:disabled) {
  transform: translateY(0);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .signup-form {
    padding: 1.5rem;
  }
  
  .signup-title {
    font-size: 1.5rem;
  }
  
  .title-section {
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .signup-title,
  .signup-subtitle {
    text-align: center;
  }
  
  .pt-logo {
    width: 45px;
    height: 45px;
  }
}
</style>
